import { useEffect, useState } from 'react'
import { Plus, Pencil, Trash2, ChevronDown, ChevronRight } from 'lucide-react'
import { toast } from 'sonner'

import { faq, type AdminFaqCategory, type AdminFaqItem } from '../api/endpoints'
import { Button } from '../components/Button'
import { Card } from '../components/Card'
import { Dialog, DialogClose, DialogContent } from '../components/Dialog'
import { FormField } from '../components/FormField'
import { Input, Textarea } from '../components/Input'
import { EmptyState } from '../components/EmptyState'
import { PageLoader } from '../components/Spinner'

export function FaqAdminPage() {
  const [data, setData] = useState<AdminFaqCategory[]>([])
  const [loading, setLoading] = useState(true)

  const [creatingCategory, setCreatingCategory] = useState(false)
  const [editingCategory, setEditingCategory] = useState<AdminFaqCategory | null>(null)
  const [creatingItemFor, setCreatingItemFor] = useState<number | null>(null)
  const [editingItem, setEditingItem] = useState<AdminFaqItem | null>(null)
  const [saving, setSaving] = useState(false)
  /* Tracks which categories are expanded. Default: all collapsed
   * unless the user has manually toggled one this session. Easier
   * than carrying per-row state for collapsed view on initial load. */
  const [expanded, setExpanded] = useState<Record<number, boolean>>({})

  function reload() {
    setLoading(true)
    faq.list()
      .then((r) => setData(r.categories))
      .catch((err) => toast.error('Failed to load FAQs', { description: err.message }))
      .finally(() => setLoading(false))
  }

  useEffect(reload, [])

  function toggle(id: number) {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }))
  }

  async function handleDeleteCategory(id: number) {
    if (!confirm('Delete this category and all its FAQ items?')) return
    try {
      await faq.deleteCategory(id)
      toast.success('Category deleted')
      reload()
    } catch (err) {
      toast.error('Failed', { description: (err as Error).message })
    }
  }

  async function handleDeleteItem(id: number) {
    if (!confirm('Delete this FAQ item?')) return
    try {
      await faq.deleteItem(id)
      toast.success('Item deleted')
      reload()
    } catch (err) {
      toast.error('Failed', { description: (err as Error).message })
    }
  }

  const totalItems = data.reduce((sum, c) => sum + c.itemCount, 0)

  if (loading) return <PageLoader />

  return (
    <div className="space-y-6 max-w-4xl">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-ink">FAQs</h1>
          <p className="text-sm text-slatey mt-1">
            {data.length} categor{data.length === 1 ? 'y' : 'ies'} · {totalItems} item{totalItems === 1 ? '' : 's'}
          </p>
        </div>
        <Button onClick={() => setCreatingCategory(true)}>
          <Plus className="size-4" />New Category
        </Button>
      </div>

      {data.length === 0 ? (
        <EmptyState
          title="No FAQ categories yet"
          description="Group your FAQs by topic. Each category can hold as many questions as you need."
          action={
            <Button onClick={() => setCreatingCategory(true)}>
              <Plus className="size-4" />New Category
            </Button>
          }
        />
      ) : (
        <div className="space-y-3">
          {data.map((c) => (
            <FaqCategoryCard
              key={c.id}
              category={c}
              isExpanded={!!expanded[c.id]}
              onToggle={() => toggle(c.id)}
              onEdit={() => setEditingCategory(c)}
              onDelete={() => handleDeleteCategory(c.id)}
              onAddItem={() => setCreatingItemFor(c.id)}
              onEditItem={(item) => setEditingItem(item)}
              onDeleteItem={handleDeleteItem}
            />
          ))}
        </div>
      )}

      {(creatingCategory || editingCategory) && (
        <CategoryDialog
          category={editingCategory}
          saving={saving}
          onSavingChange={setSaving}
          onClose={() => {
            setCreatingCategory(false)
            setEditingCategory(null)
          }}
          onSaved={reload}
        />
      )}

      {creatingItemFor !== null && (
        <ItemDialog
          item={null}
          defaultCategoryId={creatingItemFor}
          categories={data}
          saving={saving}
          onSavingChange={setSaving}
          onClose={() => setCreatingItemFor(null)}
          onSaved={reload}
        />
      )}

      {editingItem && (
        <ItemDialog
          item={editingItem}
          defaultCategoryId={editingItem.categoryId}
          categories={data}
          saving={saving}
          onSavingChange={setSaving}
          onClose={() => setEditingItem(null)}
          onSaved={reload}
        />
      )}
    </div>
  )
}

function FaqCategoryCard({
  category,
  isExpanded,
  onToggle,
  onEdit,
  onDelete,
  onAddItem,
  onEditItem,
  onDeleteItem,
}: {
  category: AdminFaqCategory
  isExpanded: boolean
  onToggle: () => void
  onEdit: () => void
  onDelete: () => void
  onAddItem: () => void
  onEditItem: (item: AdminFaqItem) => void
  onDeleteItem: (id: number) => void
}) {
  const items = category.items ?? []
  return (
    <Card>
      <div className="flex items-center justify-between gap-2 px-4 py-3">
        <button
          type="button"
          onClick={onToggle}
          className="flex items-center gap-2 text-left flex-1 min-w-0"
        >
          {isExpanded ? <ChevronDown className="size-4 text-slatey" /> : <ChevronRight className="size-4 text-slatey" />}
          <span className="font-semibold text-ink truncate">{category.name}</span>
          <span className="text-xs text-slatey shrink-0">
            {category.itemCount} item{category.itemCount === 1 ? '' : 's'}
          </span>
        </button>
        <div className="flex items-center gap-1 shrink-0">
          <Button variant="ghost" size="sm" onClick={onAddItem} title="Add item">
            <Plus className="size-3.5" />
          </Button>
          <Button variant="ghost" size="sm" onClick={onEdit} title="Edit category">
            <Pencil className="size-3.5" />
          </Button>
          <Button variant="ghost" size="sm" className="text-destructive" onClick={onDelete} title="Delete category">
            <Trash2 className="size-3.5" />
          </Button>
        </div>
      </div>

      {isExpanded && (
        <div className="border-t border-line">
          {items.length === 0 ? (
            <p className="text-sm text-slatey italic px-4 pl-8 py-3">No items yet. Click + to add one.</p>
          ) : (
            <ul className="py-1">
              {items.map((item) => (
                <li
                  key={item.id}
                  className="flex items-start justify-between gap-3 px-4 pl-8 py-1.5"
                >
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-sm text-ink">{item.question}</p>
                    <p className="text-xs text-slatey line-clamp-2 mt-0.5">{item.answer}</p>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button variant="ghost" size="sm" onClick={() => onEditItem(item)} title="Edit">
                      <Pencil className="size-3.5" />
                    </Button>
                    <Button variant="ghost" size="sm" className="text-destructive" onClick={() => onDeleteItem(item.id)} title="Delete">
                      <Trash2 className="size-3.5" />
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </Card>
  )
}

function CategoryDialog({
  category,
  saving,
  onSavingChange,
  onClose,
  onSaved,
}: {
  category: AdminFaqCategory | null
  saving: boolean
  onSavingChange: (v: boolean) => void
  onClose: () => void
  onSaved: () => void
}) {
  const [name, setName] = useState(category?.name ?? '')
  const [sortOrder, setSortOrder] = useState(String(category?.sortOrder ?? 0))

  async function handleSave() {
    onSavingChange(true)
    try {
      const payload = { name, sortOrder: parseInt(sortOrder, 10) || 0 }
      if (category) {
        await faq.updateCategory(category.id, payload)
        toast.success('Category updated')
      } else {
        /* Default new categories to the end so they don't displace
         * existing sortOrder=0 entries — easier than trying to
         * compute (max+1) inline. The admin can renumber later. */
        await faq.createCategory(payload)
        toast.success('Category created')
      }
      onClose(); onSaved()
    } catch (err) {
      toast.error('Failed', { description: (err as Error).message })
    } finally {
      onSavingChange(false)
    }
  }

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent title={category ? 'Edit Category' : 'New Category'}>
        <div className="space-y-4">
          <FormField label="Name" required>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Returns & Refunds"
              autoFocus
            />
          </FormField>
          <FormField label="Sort Order">
            <Input
              type="number"
              min={0}
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
            />
          </FormField>
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <DialogClose asChild>
            <Button variant="outline" onClick={onClose}>Cancel</Button>
          </DialogClose>
          <Button onClick={handleSave} disabled={saving || !name}>
            {saving ? 'Saving…' : category ? 'Save Changes' : 'Create'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

function ItemDialog({
  item,
  defaultCategoryId,
  categories,
  saving,
  onSavingChange,
  onClose,
  onSaved,
}: {
  item: AdminFaqItem | null
  defaultCategoryId: number
  categories: AdminFaqCategory[]
  saving: boolean
  onSavingChange: (v: boolean) => void
  onClose: () => void
  onSaved: () => void
}) {
  const [categoryId, setCategoryId] = useState(String(item?.categoryId ?? defaultCategoryId))
  const [question, setQuestion] = useState(item?.question ?? '')
  const [answer, setAnswer] = useState(item?.answer ?? '')
  const [sortOrder, setSortOrder] = useState(String(item?.sortOrder ?? 0))

  async function handleSave() {
    onSavingChange(true)
    try {
      const payload = {
        categoryId: parseInt(categoryId, 10),
        question,
        answer,
        sortOrder: parseInt(sortOrder, 10) || 0,
      }
      if (item) {
        await faq.updateItem(item.id, payload)
        toast.success('FAQ item updated')
      } else {
        await faq.createItem(payload)
        toast.success('FAQ item created')
      }
      onClose(); onSaved()
    } catch (err) {
      toast.error('Failed', { description: (err as Error).message })
    } finally {
      onSavingChange(false)
    }
  }

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent title={item ? 'Edit FAQ Item' : 'New FAQ Item'}>
        <div className="space-y-4">
          <FormField label="Category" required>
            <select
              className="w-full rounded-md border border-line bg-white px-3 py-2 text-sm"
              value={categoryId}
              onChange={(e) => setCategoryId(e.target.value)}
            >
              {categories.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </FormField>
          <FormField label="Question" required>
            <Input
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder="How long does shipping take?"
              autoFocus
            />
          </FormField>
          <FormField label="Answer" required>
            <Textarea
              value={answer}
              onChange={(e) => setAnswer(e.target.value)}
              rows={5}
              placeholder="We ship within 1–2 business days…"
            />
          </FormField>
          <FormField label="Sort Order">
            <Input
              type="number"
              min={0}
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
            />
          </FormField>
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <DialogClose asChild>
            <Button variant="outline" onClick={onClose}>Cancel</Button>
          </DialogClose>
          <Button onClick={handleSave} disabled={saving || !question || !answer || !categoryId}>
            {saving ? 'Saving…' : item ? 'Save Changes' : 'Create'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
